import React, { useState, useEffect, useRef } from 'react'
import {
  Container,
  Header,
  SpaceBetween,
  Select,
  Button,
  Box,
  Badge,
  Alert,
  ColumnLayout
} from '@cloudscape-design/components'
import { apiService } from '../services/api'
import { ColorTheme } from '../types/themes'
import { getDefaultTheme } from '../services/themeApi'
import { themeCache } from '../services/themeCache'
import { ThemeSelector } from '../components/themes/ThemeSelector'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix Leaflet default marker icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
})

const MapVisualization: React.FC = () => {
  const [selectedDataProduct, setSelectedDataProduct] = useState<any>(null)
  const [selectedAttribute, setSelectedAttribute] = useState<any>(null)
  const [locations, setLocations] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null)
  const [selectedThemeId, setSelectedThemeId] = useState<string | undefined>(undefined)
  const [activeTheme, setActiveTheme] = useState<ColorTheme | null>(null)
  const [themeLoading, setThemeLoading] = useState(false)
  const themeRef = useRef<ColorTheme | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const markersRef = useRef<L.CircleMarker[]>([])
  const referenceMarkerRef = useRef<L.CircleMarker | null>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)

  const dataProductOptions = [
    { label: 'METAR Weather', value: 'metar' },
    { label: 'Marine Conditions', value: 'marine' },
    { label: 'Trail Conditions', value: 'trail' },
    { label: 'NWS Observations', value: 'nws' },
    { label: 'HF Radio Propagation', value: 'hf_propagation' },
    { label: 'Beach Conditions', value: 'beach_conditions' }
  ]

  const attributeOptions = {
    metar: [
      { label: 'Flight Category', value: 'flightCategory' },
      { label: 'Wind Speed', value: 'windSpeed' },
      { label: 'Temperature', value: 'temperature' },
      { label: 'Visibility', value: 'visibility' }
    ],
    marine: [
      { label: 'Wave Height', value: 'waveHeight' },
      { label: 'Wind Speed', value: 'windSpeed' },
      { label: 'Water Temperature', value: 'waterTemperature' },
      { label: 'Air Temperature', value: 'airTemperature' },
      { label: 'Air Pressure', value: 'airPressure' },
      { label: 'Wave Period', value: 'wavePeriod' },
      { label: 'Wind Direction', value: 'windDirection' }
    ],
    trail: [
      { label: 'Trail Condition', value: 'condition' },
      { label: 'Snow Depth', value: 'snowDepth' },
      { label: 'Temperature', value: 'temperature' }
    ],
    nws: [
      { label: 'Temperature', value: 'temperature' },
      { label: 'Wind Speed', value: 'windSpeed' },
      { label: 'Relative Humidity', value: 'relativeHumidity' },
      { label: 'Visibility', value: 'visibility' },
      { label: 'Wind Direction', value: 'windDirection' }
    ],
    hf_propagation: [
      { label: '160m (1.8 MHz)', value: 'band_160m' },
      { label: '80m (3.5 MHz)', value: 'band_80m' },
      { label: '40m (7.0 MHz)', value: 'band_40m' },
      { label: '30m (10.1 MHz)', value: 'band_30m' },
      { label: '20m (14.0 MHz)', value: 'band_20m' },
      { label: '17m (18.1 MHz)', value: 'band_17m' },
      { label: '15m (21.0 MHz)', value: 'band_15m' },
      { label: '10m (28.0 MHz)', value: 'band_10m' }
    ],
    beach_conditions: [
      { label: 'Swim Risk', value: 'swimRisk' },
      { label: 'Rip Current', value: 'ripCurrent' },
      { label: 'Surf Height', value: 'surfHeight' },
      { label: 'Water Temperature', value: 'waterTemperature' },
      { label: 'UV Index', value: 'uvIndex' },
      { label: 'Thunderstorm', value: 'thunderstorm' }
    ]
  }

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return

    // Create map centered on Wisconsin with zoom controls
    const map = L.map(mapContainerRef.current, {
      center: [44.5, -89.5],
      zoom: 7,
      zoomControl: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      dragging: true
    })

    // Add map tiles (CARTO light basemap with English labels)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>',
      maxZoom: 19,
      minZoom: 3
    }).addTo(map)

    // Add US state boundaries from public GeoJSON source
    fetch('https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json')
      .then(response => response.json())
      .then(data => {
        L.geoJSON(data, {
          style: {
            color: '#3388ff',
            weight: 2,
            opacity: 0.6,
            fillOpacity: 0.1
          },
          onEachFeature: (feature, layer) => {
            if (feature.properties && feature.properties.name) {
              layer.bindTooltip(feature.properties.name, {
                permanent: false,
                direction: 'center',
                className: 'state-label'
              })
            }
          }
        }).addTo(map)
      })
      .catch(err => console.error('Error loading state boundaries:', err))

    mapRef.current = map

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [])

  // Synchronize themeRef with activeTheme state
  useEffect(() => {
    themeRef.current = activeTheme
  }, [activeTheme])

  // Add moveend event listener when data product/attribute changes
  useEffect(() => {
    if (!mapRef.current) return

    const handleMoveEnd = () => {
      if (selectedDataProduct && selectedAttribute) {
        // HF Propagation data is not bounds-based, skip reload on pan/zoom
        if (selectedDataProduct.value === 'hf_propagation') return
        loadVisibleLocations()
      }
    }

    mapRef.current.on('moveend', handleMoveEnd)

    return () => {
      if (mapRef.current) {
        mapRef.current.off('moveend', handleMoveEnd)
      }
    }
  }, [selectedDataProduct, selectedAttribute])

  // Load data when product, attribute, AND theme are ready
  useEffect(() => {
    if (selectedDataProduct && selectedAttribute && activeTheme && mapRef.current) {
      // Invalidate size to ensure map renders correctly when becoming visible
      mapRef.current.invalidateSize()
      
      // Load data now that theme is ready
      loadVisibleLocations()
    }
  }, [selectedDataProduct, selectedAttribute, activeTheme])

  const fetchTheme = async (): Promise<ColorTheme | null> => {
    if (!selectedDataProduct || !selectedAttribute) return null

    setThemeLoading(true)
    try {
      // Check cache first
      const cachedTheme = themeCache.getTheme(
        selectedDataProduct.value,
        selectedAttribute.value
      )
      
      if (cachedTheme) {
        setActiveTheme(cachedTheme)
        themeRef.current = cachedTheme
        setSelectedThemeId(cachedTheme.theme_id)
        return cachedTheme
      }

      // Fetch from API
      const theme = await getDefaultTheme(
        selectedDataProduct.value,
        selectedAttribute.value
      )
      
      // Update all sources (state, ref, cache)
      setActiveTheme(theme)
      themeRef.current = theme
      themeCache.setTheme(theme)
      setSelectedThemeId(theme.theme_id)
      
      // Update markers with new theme if locations are already loaded
      if (locations.length > 0) {
        updateMarkers(locations)
      }
      
      return theme
    } catch (error) {
      console.error('Error fetching theme:', error)
      // Fall back to grayscale theme
      const fallbackTheme = createGrayscaleTheme()
      setActiveTheme(fallbackTheme)
      themeRef.current = fallbackTheme
      setAlert({
        type: 'error',
        message: 'Failed to load color theme. Using default grayscale.'
      })
      return fallbackTheme
    } finally {
      setThemeLoading(false)
    }
  }

  const createGrayscaleTheme = (): ColorTheme => {
    return {
      theme_id: 'grayscale-fallback',
      theme_name: 'Grayscale (Fallback)',
      data_product: selectedDataProduct?.value || '',
      attribute: selectedAttribute?.value || '',
      theme_type: 'system',
      is_default: false,
      mapping_type: 'categorical',
      mappings: [
        { category: 'default', color: '#9ca3af', label: 'No Data' }
      ],
      version: 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
  }

  const handleThemeChange = (themeId: string, theme: ColorTheme) => {
    setSelectedThemeId(themeId)
    setActiveTheme(theme)
    themeRef.current = theme
    
    // Update cache
    if (selectedDataProduct && selectedAttribute) {
      themeCache.setTheme(theme)
    }
    
    // Update markers with new theme
    if (locations.length > 0) {
      updateMarkers(locations)
    }
  }

  const loadHfPropagationData = async () => {
    if (!mapRef.current) return

    try {
      console.log('Loading HF propagation data via default locations + map API')

      // Fetch default HF locations for canonical names and coordinates (Req 4.1)
      const defaultsResponse = await apiService.getDefaultHfLocations()
      let defaultLocations: any[] = []
      let defaultReferenceLocation: any = null

      if (defaultsResponse.statusCode === 200 && defaultsResponse.data) {
        const defaultsData = defaultsResponse.data as any
        if (defaultsData.status === 'success') {
          defaultLocations = defaultsData.locations || []
          defaultReferenceLocation = defaultsData.reference_location || null
          console.log('Default HF locations loaded:', defaultLocations.length)
        }
      }

      // Also fetch propagation data from the map API for band conditions
      const response = await apiService.getMapLocations('hf_propagation', {
        minLat: -90,
        maxLat: 90,
        minLng: -180,
        maxLng: 180
      })

      console.log('HF propagation API response:', response)

      // Build a lookup of propagation data by location_id for merging
      const propagationDataMap: Record<string, any> = {}
      let mapReferenceLocation: any = null

      if (response.statusCode === 200 && response.data) {
        const responseData = response.data as any
        if (responseData.status === 'success') {
          const hfLocations = responseData.locations || []
          mapReferenceLocation = responseData.reference_location || null
          hfLocations.forEach((loc: any) => {
            if (loc.location_id) {
              propagationDataMap[loc.location_id] = loc
            }
            // Also index by coordinate key for matching default locations
            if (loc.coordinates) {
              const coordKey = `${loc.coordinates.latitude}_${loc.coordinates.longitude}`
              propagationDataMap[coordKey] = loc
            }
          })
        }
      }

      // Use the reference location from defaults, fall back to map API
      const referenceLocation = defaultReferenceLocation || mapReferenceLocation

      // Merge default locations with propagation data
      let locationsWithData: any[]

      if (defaultLocations.length > 0) {
        // Use default locations as the base, enrich with propagation data (Req 4.1, 4.2)
        locationsWithData = defaultLocations.map((defLoc: any) => {
          const coordKey = `${defLoc.latitude}_${defLoc.longitude}`
          const locationId = defLoc.locationId || defLoc.location_id || coordKey
          // Match propagation data by location_id or coordinate key
          const propData = propagationDataMap[locationId] || propagationDataMap[coordKey] || null

          return {
            locationId: locationId,
            name: defLoc.commonName || defLoc.name || locationId,
            coordinates: {
              latitude: parseFloat(defLoc.latitude),
              longitude: parseFloat(defLoc.longitude),
            },
            payload: propData ? {
              [selectedAttribute?.value || 'band_20m']: propData.attenuation_db,
              _bands: propData.bands || [],
              _path_haf_maximum: propData.path_haf_maximum,
              _attenuation_db: propData.attenuation_db,
              _color: propData.color,
            } : null,
            _hfData: propData,
            _isHfPropagation: true,
          }
        })
      } else {
        // Fallback: use map API locations directly if defaults unavailable
        if (response.statusCode === 200 && response.data) {
          const responseData = response.data as any
          if (responseData.status === 'success') {
            const hfLocations = responseData.locations || []
            locationsWithData = hfLocations.map((loc: any) => ({
              locationId: loc.location_id,
              name: loc.location_id,
              coordinates: loc.coordinates || null,
              payload: {
                [selectedAttribute?.value || 'band_20m']: loc.attenuation_db,
                _bands: loc.bands || [],
                _path_haf_maximum: loc.path_haf_maximum,
                _attenuation_db: loc.attenuation_db,
                _color: loc.color,
              },
              _hfData: loc,
              _isHfPropagation: true,
            }))
          } else {
            locationsWithData = []
            setAlert({
              type: 'error',
              message: responseData.error || 'Failed to load HF propagation data'
            })
          }
        } else {
          locationsWithData = []
          setAlert({
            type: 'error',
            message: response.error || 'Failed to load HF propagation data'
          })
        }
      }

      console.log('HF propagation locations:', locationsWithData.length)

      setLocations(locationsWithData)
      updateHfMarkers(locationsWithData, referenceLocation)

      if (mapRef.current && locationsWithData.length > 0) {
        mapRef.current.setView([39.8, -98.5], 4)
      }
    } catch (error) {
      console.error('Error loading HF propagation data:', error)
      setAlert({
        type: 'error',
        message: 'Failed to load HF propagation data. Please try again.'
      })
    } finally {
      setLoading(false)
    }
  }

  const updateHfMarkers = (locations: any[], referenceLocation: any) => {
    if (!mapRef.current) return

    const resolvedTheme =
      themeRef.current ||
      activeTheme ||
      (selectedDataProduct && selectedAttribute
        ? themeCache.getTheme(selectedDataProduct.value, selectedAttribute.value)
        : null)

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove())
    markersRef.current = []
    if (referenceMarkerRef.current) {
      referenceMarkerRef.current.remove()
      referenceMarkerRef.current = null
    }

    // Add reference location marker with distinct styling
    if (referenceLocation && referenceLocation.latitude && referenceLocation.longitude) {
      const refMarker = L.circleMarker(
        [parseFloat(referenceLocation.latitude), parseFloat(referenceLocation.longitude)],
        {
          radius: 12,
          fillColor: '#FFD700',
          color: '#B8860B',
          weight: 3,
          opacity: 1,
          fillOpacity: 0.9
        }
      )
      refMarker.bindPopup(`
        <div style="font-family: sans-serif;">
          <strong>⭐ Reference Location (QTH)</strong><br/>
          <small>Lat: ${referenceLocation.latitude}, Lon: ${referenceLocation.longitude}</small>
        </div>
      `)
      refMarker.addTo(mapRef.current)
      referenceMarkerRef.current = refMarker
    }

    // Add target location markers
    locations.forEach(location => {
      if (!location.coordinates) return

      const hfData = location._hfData
      const selectedBandAttr = selectedAttribute?.value || 'band_20m'

      // Get attenuation for the selected band from bands array
      let attenuation: number | null = null
      if (hfData?.bands && Array.isArray(hfData.bands)) {
        const bandName = selectedBandAttr.replace('band_', '')
        const bandResult = hfData.bands.find((b: any) => b.band === bandName)
        if (bandResult) {
          attenuation = bandResult.attenuation_db
        }
      }

      // Determine color: prefer SNR-margin color, fall back to HAF color, then theme
      let color = '#999'
      if (hfData?.snr_color) {
        // Use SNR-margin-based color from link budget
        color = hfData.snr_color

        // Override with per-band SNR color if the selected band has data
        if (hfData?.bands && Array.isArray(hfData.bands)) {
          const bandName = selectedBandAttr.replace('band_', '')
          const bandResult = hfData.bands.find((b: any) => b.band === bandName)
          if (bandResult) {
            if (bandResult.propagation === 'no_propagation') {
              color = '#9ca3af'
            } else if (typeof bandResult.snr_margin_db === 'number') {
              color = bandResult.snr_margin_db > 10 ? '#22c55e'
                : bandResult.snr_margin_db >= 0 ? '#eab308'
                : '#ef4444'
            }
          }
        }
      } else if (attenuation !== null && resolvedTheme) {
        color = themeCache.getColorForValue(resolvedTheme, attenuation)
      } else if (hfData?.haf_color) {
        color = hfData.haf_color
      } else if (hfData?.color) {
        color = hfData.color
      }

      const marker = L.circleMarker(
        [location.coordinates.latitude, location.coordinates.longitude],
        {
          radius: 8,
          fillColor: color,
          color: '#333',
          weight: 1,
          opacity: 1,
          fillOpacity: 0.8
        }
      )

      // Build popup with all 8 band conditions
      const popupContent = getHfPopupContent(location.locationId, hfData)
      marker.bindPopup(`
        <div style="font-family: sans-serif;">
          <strong>${location.name || location.locationId}</strong><br/>
          <small>${location.locationId}</small><br/>
          ${popupContent}
        </div>
      `)

      marker.addTo(mapRef.current!)
      markersRef.current.push(marker)
    })
  }

  const getHfPopupContent = (locationId: string, hfData: any): string => {
    if (!hfData) return 'No data available'

    const lines: string[] = []

    // Link budget summary (new fields)
    if (hfData.distance_km !== undefined) {
      lines.push(`<strong>Distance:</strong> ${Number(hfData.distance_km).toLocaleString()} km`)
    }
    if (hfData.tx_state || hfData.rx_state) {
      const tx = (hfData.tx_state || 'N/A').charAt(0).toUpperCase() + (hfData.tx_state || '').slice(1)
      const rx = (hfData.rx_state || 'N/A').charAt(0).toUpperCase() + (hfData.rx_state || '').slice(1)
      lines.push(`<strong>TX:</strong> ${tx} &nbsp; <strong>RX:</strong> ${rx}`)
    }
    if (hfData.muf_mhz !== undefined) {
      lines.push(`<strong>MUF:</strong> ${Number(hfData.muf_mhz).toFixed(1)} MHz`)
    }
    if (hfData.num_hops !== undefined) {
      lines.push(`<strong>Hops:</strong> ${hfData.num_hops}`)
    }

    // Show Path HAF Maximum if available
    if (hfData.path_haf_max !== undefined && hfData.path_haf_max !== null) {
      lines.push(`<strong>Path HAF Max:</strong> ${Number(hfData.path_haf_max).toFixed(2)} MHz`)
    } else if (hfData.path_haf_maximum !== undefined && hfData.path_haf_maximum !== null) {
      lines.push(`<strong>Path HAF Max:</strong> ${Number(hfData.path_haf_maximum).toFixed(2)} MHz`)
    }

    // Show all 8 band conditions with SNR margin
    if (hfData.bands && Array.isArray(hfData.bands) && hfData.bands.length > 0) {
      lines.push('<br/><strong>Band Conditions:</strong>')
      const bandLabels: Record<string, string> = {
        '160m': '160m', '80m': '80m', '40m': '40m',
        '20m': '20m', '17m': '17m', '15m': '15m',
        '12m': '12m', '10m': '10m',
      }
      for (const band of hfData.bands) {
        const label = bandLabels[band.band] || band.band

        if (band.propagation === 'no_propagation') {
          lines.push(
            `<span style="color:#9ca3af;">●</span> ${label}: <span style="color:#9ca3af;">Above MUF</span>`
          )
        } else if (typeof band.snr_margin_db === 'number') {
          const snr = band.snr_margin_db
          const snrColor = snr > 10 ? '#22c55e' : snr >= 0 ? '#eab308' : '#ef4444'
          lines.push(
            `<span style="color:${snrColor};">●</span> ${label}: <strong style="color:${snrColor};">${snr > 0 ? '+' : ''}${snr.toFixed(1)} dB</strong>`
          )
        } else {
          // Fallback to legacy attenuation display
          const atten = typeof band.attenuation_db === 'number'
            ? band.attenuation_db.toFixed(2) + ' dB atten'
            : 'N/A'
          lines.push(`<span style="color:#999;">●</span> ${label}: ${atten}`)
        }
      }
    }

    return lines.length > 0 ? lines.join('<br/>') : 'No data available'
  }

  const loadVisibleLocations = async () => {
    if (!mapRef.current || !selectedDataProduct) return

    setLoading(true)
    setAlert(null)

    try {
      // HF Propagation uses the device data API (grid-based, not point-based)
      if (selectedDataProduct.value === 'hf_propagation') {
        await loadHfPropagationData()
        return
      }

      const bounds = mapRef.current.getBounds()
      const minLat = bounds.getSouth()
      const maxLat = bounds.getNorth()
      const minLng = bounds.getWest()
      const maxLng = bounds.getEast()

      console.log('Loading map data:', {
        dataProduct: selectedDataProduct.value,
        bounds: { minLat, maxLat, minLng, maxLng }
      })

      // Use the new map API endpoint
      const response = await apiService.getMapLocations(selectedDataProduct.value, {
        minLat,
        maxLat,
        minLng,
        maxLng
      })

      console.log('Map API response:', response)

      if (response.statusCode === 200 && response.data) {
        const responseData = response.data as any
        
        console.log('Response data:', responseData)
        
        if (responseData.status === 'success' && Array.isArray(responseData.locations)) {
          const locationsWithData = responseData.locations.map((loc: any) => ({
            locationId: loc.location_id,
            name: loc.commonName || loc.name,
            coordinates: loc.coordinates,
            payload: loc.data?.payload || null
          }))
          
          console.log('Locations with data:', locationsWithData.length, locationsWithData.slice(0, 3))
          
          setLocations(locationsWithData)
          updateMarkers(locationsWithData)
        } else {
          console.error('Invalid response format:', responseData)
          setAlert({
            type: 'error',
            message: responseData.error || 'Failed to load location data'
          })
        }
      } else {
        console.error('API error:', response)
        setAlert({
          type: 'error',
          message: response.error || 'Failed to load location data'
        })
      }
    } catch (error) {
      console.error('Error loading location data:', error)
      setAlert({
        type: 'error',
        message: 'Failed to load location data. Please try again.'
      })
    } finally {
      setLoading(false)
    }
  }

  const updateMarkers = (locations: any[]) => {
    if (!mapRef.current) return

    // Resolve theme from multiple sources (priority order)
    const resolvedTheme = 
      themeRef.current || 
      activeTheme || 
      (selectedDataProduct && selectedAttribute 
        ? themeCache.getTheme(selectedDataProduct.value, selectedAttribute.value)
        : null)

    if (!resolvedTheme) {
      console.warn('No theme available for marker rendering')
      return // Don't render markers without a theme
    }

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove())
    markersRef.current = []
    if (referenceMarkerRef.current) {
      referenceMarkerRef.current.remove()
      referenceMarkerRef.current = null
    }

    // Add new markers with resolved theme
    locations.forEach(location => {
      if (!location.coordinates) return

      const color = getLocationColor(
        location, 
        selectedAttribute?.value, 
        selectedDataProduct?.value,
        resolvedTheme
      )
      
      const marker = L.circleMarker(
        [location.coordinates.latitude, location.coordinates.longitude],
        {
          radius: 8,
          fillColor: color,
          color: '#333',
          weight: 1,
          opacity: 1,
          fillOpacity: 0.8
        }
      )

      // Add popup with location info
      const popupContent = `
        <div style="font-family: sans-serif;">
          <strong>${location.name}</strong><br/>
          <small>${location.locationId}</small><br/>
          ${getPopupContent(location, selectedAttribute?.value, selectedDataProduct?.value)}
        </div>
      `
      marker.bindPopup(popupContent)

      marker.addTo(mapRef.current!)
      markersRef.current.push(marker)
    })
  }

  const getLocationColor = (location: any, attribute: string, product: string, theme: ColorTheme): string => {
    if (!location.payload || !theme) return '#999'

    // Get the value for the selected attribute
    let value = location.payload[attribute]
    
    if (value === null || value === undefined) return '#999'

    // Use theme cache to determine color
    return themeCache.getColorForValue(theme, value)
  }

  const getPopupContent = (location: any, attribute: string, product: string): string => {
    if (!location.payload) return 'No data available'

    const formatValue = (value: any, unit: string = ''): string => {
      if (value === null || value === undefined) return 'N/A'
      return `${value}${unit}`
    }

    const getCompassDirection = (degrees: number): string => {
      if (degrees >= 337.5 || degrees < 22.5) return 'N'
      if (degrees >= 22.5 && degrees < 67.5) return 'NE'
      if (degrees >= 67.5 && degrees < 112.5) return 'E'
      if (degrees >= 112.5 && degrees < 157.5) return 'SE'
      if (degrees >= 157.5 && degrees < 202.5) return 'S'
      if (degrees >= 202.5 && degrees < 247.5) return 'SW'
      if (degrees >= 247.5 && degrees < 292.5) return 'W'
      return 'NW'
    }

    switch (product) {
      case 'metar':
        const metarData = []
        
        // Flight Category
        if (location.payload.flightCategory) {
          metarData.push(`<strong>Flight Category:</strong> ${location.payload.flightCategory}`)
        }
        
        // Wind
        const windSpeed = location.payload.windSpeed
        const windDir = location.payload.windDirection
        if (windSpeed !== null && windSpeed !== undefined) {
          const dirStr = windDir !== null && windDir !== undefined 
            ? ` from ${Math.round(windDir)}° (${getCompassDirection(windDir)})`
            : ''
          metarData.push(`<strong>Wind:</strong> ${windSpeed} kt${dirStr}`)
        }
        
        // Temperature & Dew Point
        if (location.payload.temperature !== null && location.payload.temperature !== undefined) {
          metarData.push(`<strong>Temperature:</strong> ${location.payload.temperature}°C`)
        }
        if (location.payload.dewpoint !== null && location.payload.dewpoint !== undefined) {
          metarData.push(`<strong>Dew Point:</strong> ${location.payload.dewpoint}°C`)
        }
        
        // Altimeter
        if (location.payload.altimeter !== null && location.payload.altimeter !== undefined) {
          metarData.push(`<strong>Altimeter:</strong> ${location.payload.altimeter} inHg`)
        }
        
        // Visibility
        if (location.payload.visibility !== null && location.payload.visibility !== undefined) {
          metarData.push(`<strong>Visibility:</strong> ${location.payload.visibility} mi`)
        }
        
        // Conditions
        if (location.payload.conditions && location.payload.conditions.length > 0) {
          metarData.push(`<strong>Conditions:</strong> ${location.payload.conditions.join(', ')}`)
        }
        
        return metarData.length > 0 ? metarData.join('<br/>') : 'No data available'
        
      case 'marine':
        const marineData = []
        
        // Station Type
        if (location.payload.stationType) {
          marineData.push(`<strong>Type:</strong> ${location.payload.stationType}`)
        }
        
        // Wind
        const mWindSpeed = location.payload.windSpeed
        const mWindDir = location.payload.windDirection
        if (mWindSpeed !== null && mWindSpeed !== undefined) {
          const dirStr = mWindDir !== null && mWindDir !== undefined 
            ? ` from ${Math.round(mWindDir)}° (${getCompassDirection(mWindDir)})`
            : ''
          marineData.push(`<strong>Wind:</strong> ${mWindSpeed} kt${dirStr}`)
        }
        
        // Temperatures
        if (location.payload.airTemperature !== null && location.payload.airTemperature !== undefined) {
          marineData.push(`<strong>Air Temp:</strong> ${location.payload.airTemperature}°F`)
        }
        if (location.payload.waterTemperature !== null && location.payload.waterTemperature !== undefined) {
          marineData.push(`<strong>Water Temp:</strong> ${location.payload.waterTemperature}°F`)
        }
        
        // Air Pressure
        if (location.payload.airPressure !== null && location.payload.airPressure !== undefined) {
          marineData.push(`<strong>Pressure:</strong> ${location.payload.airPressure} hPa`)
        }
        
        // Wave Data
        if (location.payload.waveHeight !== null && location.payload.waveHeight !== undefined) {
          marineData.push(`<strong>Wave Height:</strong> ${location.payload.waveHeight} ft`)
        }
        if (location.payload.wavePeriod !== null && location.payload.wavePeriod !== undefined) {
          marineData.push(`<strong>Wave Period:</strong> ${location.payload.wavePeriod} sec`)
        }
        
        return marineData.length > 0 ? marineData.join('<br/>') : 'No data available'
        
      case 'trail':
        if (attribute === 'condition') {
          return `Condition: <strong>${location.payload.condition || 'Unknown'}</strong>`
        }
        break

      case 'nws':
        const nwsData = []

        // Station Name
        if (location.name) {
          nwsData.push(`<strong>Station:</strong> ${location.name}`)
        }

        // Temperature
        if (location.payload.temperature !== null && location.payload.temperature !== undefined) {
          nwsData.push(`<strong>Temperature:</strong> ${location.payload.temperature}°C`)
        }

        // Wind
        const nwsWindSpeed = location.payload.windSpeed
        const nwsWindDir = location.payload.windDirection
        if (nwsWindSpeed !== null && nwsWindSpeed !== undefined) {
          const dirStr = nwsWindDir !== null && nwsWindDir !== undefined
            ? ` from ${Math.round(nwsWindDir)}° (${getCompassDirection(nwsWindDir)})`
            : ''
          nwsData.push(`<strong>Wind:</strong> ${nwsWindSpeed} km/h${dirStr}`)
        } else if (nwsWindDir !== null && nwsWindDir !== undefined) {
          nwsData.push(`<strong>Wind Direction:</strong> ${Math.round(nwsWindDir)}° (${getCompassDirection(nwsWindDir)})`)
        }

        // Relative Humidity
        if (location.payload.relativeHumidity !== null && location.payload.relativeHumidity !== undefined) {
          nwsData.push(`<strong>Humidity:</strong> ${location.payload.relativeHumidity}%`)
        }

        // Visibility
        if (location.payload.visibility !== null && location.payload.visibility !== undefined) {
          nwsData.push(`<strong>Visibility:</strong> ${location.payload.visibility} m`)
        }

        // Text Description
        if (location.payload.textDescription) {
          nwsData.push(`<strong>Conditions:</strong> ${location.payload.textDescription}`)
        }

        return nwsData.length > 0 ? nwsData.join('<br/>') : 'No data available'

      case 'hf_propagation':
        // HF propagation popups are handled by getHfPopupContent via updateHfMarkers
        // This case handles the fallback if called through the standard path
        if (location._hfData) {
          return getHfPopupContent(location.locationId, location._hfData)
        }
        return 'No HF propagation data available'

      case 'beach_conditions':
        const beachData: string[] = []
        if (location.payload.swimRisk) {
          const riskColor = location.payload.swimRisk === 'Low' ? '🟢' : location.payload.swimRisk === 'Moderate' ? '🟡' : '🔴'
          beachData.push(`<strong>Swim Risk:</strong> ${riskColor} ${location.payload.swimRisk}`)
        }
        if (location.payload.surfHeight) {
          beachData.push(`<strong>Surf:</strong> ${location.payload.surfHeight}`)
        }
        if (location.payload.waterTemperature) {
          beachData.push(`<strong>Water Temp:</strong> ${location.payload.waterTemperature}`)
        }
        if (location.payload.uvIndex) {
          beachData.push(`<strong>UV Index:</strong> ${location.payload.uvIndex}`)
        }
        if (location.payload.winds) {
          beachData.push(`<strong>Winds:</strong> ${location.payload.winds}`)
        }
        if (location.payload.weather) {
          beachData.push(`<strong>Weather:</strong> ${location.payload.weather}`)
        }
        if (location.payload.thunderstorm && location.payload.thunderstorm !== 'None') {
          beachData.push(`<strong>Thunderstorm:</strong> ${location.payload.thunderstorm}`)
        }
        return beachData.length > 0 ? beachData.join('<br/>') : 'No data available'
    }
    return 'Data available'
  }

  const getColorLegend = (): Array<{color: string, label: string}> => {
    // HF Propagation uses a fixed SNR-margin color scale, not the theme
    if (selectedDataProduct?.value === 'hf_propagation') {
      return [
        { color: '#22c55e', label: 'Solid (SNR > 10 dB)' },
        { color: '#eab308', label: 'Marginal (0–10 dB)' },
        { color: '#ef4444', label: 'Unlikely (< 0 dB)' },
        { color: '#9ca3af', label: 'Above MUF' },
      ]
    }

    if (!activeTheme) return []

    // Use theme cache to get legend items
    return themeCache.getLegendItems(activeTheme)
  }

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Map Visualization</Header>

      {alert && (
        <Alert
          type={alert.type}
          dismissible
          onDismiss={() => setAlert(null)}
          data-testid="error-alert"
        >
          {alert.message}
        </Alert>
      )}

      <Container>
        <SpaceBetween size="l">
          <ColumnLayout columns={4}>
            <div>
              <Box variant="awsui-key-label">Data Product</Box>
              <Select
                selectedOption={selectedDataProduct}
                onChange={({ detail }) => {
                  setSelectedDataProduct(detail.selectedOption)
                  setSelectedAttribute(null)
                  setActiveTheme(null)
                  setSelectedThemeId(undefined)
                }}
                options={dataProductOptions}
                placeholder="Select a data product"
                data-testid="data-product-select"
              />
            </div>
            <div>
              <Box variant="awsui-key-label">Color Attribute</Box>
              <Select
                selectedOption={selectedAttribute}
                onChange={({ detail }) => {
                  setSelectedAttribute(detail.selectedOption)
                  setActiveTheme(null)
                  setSelectedThemeId(undefined)
                }}
                options={selectedDataProduct ? attributeOptions[selectedDataProduct.value as keyof typeof attributeOptions] || [] : []}
                placeholder="Select an attribute"
                disabled={!selectedDataProduct}
                data-testid="color-attribute-select"
              />
            </div>
            <div>
              <Box variant="awsui-key-label">Color Theme</Box>
              <ThemeSelector
                dataProduct={selectedDataProduct?.value || ''}
                attribute={selectedAttribute?.value || ''}
                selectedThemeId={selectedThemeId}
                onThemeChange={handleThemeChange}
                disabled={!selectedDataProduct || !selectedAttribute}
              />
            </div>
            <div>
              <Box variant="awsui-key-label">Actions</Box>
              <Button
                onClick={() => loadVisibleLocations()}
                loading={loading}
                disabled={!selectedDataProduct || !selectedAttribute}
              >
                Refresh Data
              </Button>
            </div>
          </ColumnLayout>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Interactive Map</Header>}>
        <div
          ref={mapContainerRef}
          style={{
            width: '100%',
            height: '600px',
            borderRadius: '8px',
            border: '1px solid #ddd',
            display: selectedDataProduct && selectedAttribute ? 'block' : 'none'
          }}
        />
        {!selectedDataProduct || !selectedAttribute ? (
          <Box
            textAlign="center"
            color="inherit"
            padding="xxl"
          >
            <div style={{
              backgroundColor: '#f8f9fa',
              border: '2px dashed #dee2e6',
              borderRadius: '8px',
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Box variant="h2" color="text-body-secondary">
                🗺️ Interactive Map
              </Box>
              <Box variant="p" color="text-body-secondary">
                <div style={{ maxWidth: '400px', marginTop: '16px' }}>
                  Select a data product and attribute to view the interactive map with real-time data visualization.
                  Zoom and pan to explore different areas.
                </div>
              </Box>
            </div>
          </Box>
        ) : null}
      </Container>

      {selectedDataProduct && selectedAttribute && (
        <Container header={<Header variant="h2">Color Legend</Header>}>
          <SpaceBetween size="s">
            {getColorLegend().map((item, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: item.color,
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}
                />
                <span>{item.label}</span>
              </div>
            ))}
          </SpaceBetween>
        </Container>
      )}

      <Container header={<Header variant="h2">Statistics</Header>}>
        <ColumnLayout columns={3} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Visible Locations</Box>
            <Box variant="h2" color="text-status-info">{locations.length}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Selected Product</Box>
            <Box variant="h2" color="text-status-success">
              {selectedDataProduct?.label || 'None'}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Last Updated</Box>
            <Box variant="h2" color="text-status-warning">
              {loading ? 'Loading...' : 'Just now'}
            </Box>
          </div>
        </ColumnLayout>
      </Container>
    </SpaceBetween>
  )
}

export default MapVisualization
